import React, { useEffect, useRef, useState } from 'react';
import { Mail } from 'lucide-react';
import { getThemeColor } from '../../config/appConfig';
import NewtonsCradleLoader from '../../components/common/NewtonsCradleLoader';

const THEME = getThemeColor('primary');

const MANAGER_INFO_TEXT =
  'Get to know your partner account manager and feel free to contact them with any questions you may have via one of the methods listed below.';

const WhatsAppIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 3.5a8.5 8.5 0 0 0-7.3 12.8L3.5 20.5l4.4-1.1A8.5 8.5 0 1 0 12 3.5zm0 15.3a6.8 6.8 0 0 1-3.5-1l-.3-.1-2.5.6.7-2.4-.2-.3a6.8 6.8 0 1 1 5.3 3.2zm3.7-4.8c-.2-.1-1.3-.6-1.5-.7-.2-.1-.4-.1-.6.1-.1.1-.5.5-.6.7-.1.1-.2.2-.4.1-.2-.1-.8-.3-1.6-1-.6-.5-1-1.2-1.1-1.4-.1-.2 0-.3.1-.5.1-.1.2-.3.3-.4.1-.1.1-.2.2-.3 0-.1 0-.2-.1-.3 0-.1-.5-1.1-.7-1.5-.2-.3-.4-.4-.7-.4h-.6c-.2 0-.5.1-.6.3-.2.2-.7.7-.7 1.5 0 .9.7 1.8.8 2 .1.2 1.4 2.2 3.5 3 .5.2.9.3 1.2.3.4 0 .8-.1 1.1-.4.3-.3 1.2-1.1 1.4-1.4.2-.3 0-.5 0-.6-.1-.1-.2-.1-.4-.2z" />
  </svg>
);

const DashboardAccountManager = ({ manager, loading }) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const infoRef = useRef(null);

  const email = manager?.email || '';
  const phone = manager?.phone || '';
  const name = manager?.name || '';

  const mailHref = email ? `mailto:${email}` : undefined;
  const phoneDigits = phone.replace(/[^\d+]/g, '');
  const waHref = phoneDigits
    ? `https://wa.me/${phoneDigits.replace(/^\+/, '')}`
    : undefined;

  useEffect(() => {
    if (!infoOpen) return undefined;
    const onPointerDown = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) {
        setInfoOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setInfoOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [infoOpen]);

  return (
    <section className="dash-side-card">
      <div className="dash-side-card__head">
        <h2 className="dash-side-card__title">Account Manager Info</h2>
        <div
          className="relative"
          ref={infoRef}
          onMouseEnter={() => setInfoOpen(true)}
          onMouseLeave={() => setInfoOpen(false)}
        >
          <button
            type="button"
            onClick={() => setInfoOpen((v) => !v)}
            className="w-5 h-5 rounded-full bg-gray-900 text-white text-[11px] font-bold leading-none flex items-center justify-center hover:bg-black transition"
            aria-label="Account manager information"
            aria-expanded={infoOpen}
          >
            i
          </button>
          {infoOpen && (
            <div
              role="tooltip"
              className="absolute right-0 top-full z-20 pt-2.5 w-[min(280px,calc(100vw-3rem))]"
            >
              <div className="relative rounded-md border border-gray-200 bg-white px-3 py-2.5 text-[11px] leading-relaxed text-gray-700 shadow-md">
                <span
                  className="absolute -top-1.5 right-3 w-3 h-3 rotate-45 border-l border-t border-gray-200 bg-white"
                  aria-hidden
                />
                {MANAGER_INFO_TEXT}
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <NewtonsCradleLoader size="24px" />
        </div>
      ) : !manager ? (
        <div className="dash-empty" style={{ padding: '2rem 1rem' }}>
          No account manager assigned
        </div>
      ) : (
        <div className="px-4 py-4">
          {name ? (
            <p className="text-center text-sm font-semibold text-gray-900 mb-3">{name}</p>
          ) : null}

          <div className="divide-y divide-gray-100">
            <div className="flex items-center gap-3 py-3">
              <Mail className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-xs text-gray-500 flex-1">Email</span>
              {email ? (
                <a
                  href={mailHref}
                  className="text-sm font-semibold truncate max-w-[55%] hover:underline"
                  style={{ color: THEME }}
                >
                  {email}
                </a>
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </div>
            <div className="flex items-center gap-3 py-3">
              <span className="text-[#25D366] shrink-0">
                <WhatsAppIcon />
              </span>
              <span className="text-xs text-gray-500 flex-1">WhatsApp</span>
              {phone ? (
                <a
                  href={waHref}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-semibold truncate max-w-[55%] hover:underline"
                  style={{ color: THEME }}
                >
                  {phone}
                </a>
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DashboardAccountManager;
